import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, LoadingSpinner, SelectField } from 'components';
import {
  FaCheckCircle,
  FaLinkedin,
  FaSync,
  FaTimesCircle,
} from 'react-icons/fa';
import { useTanstackMutation } from 'hooks';
import { API_PATHS } from 'api';
import { queryClient } from 'react-query';
import { Lead, LeadApiResponse } from 'models/Lead';
import type { LeadCandidate } from 'models/LeadCandidate';

import './LeadSelect.styles.scss';

type LeadSelectProps = {
  lead: Lead;
  hasLead?: boolean;
  leadOptions: LeadCandidate[];
  loadingLeadsInProgress: boolean;
  error: Error | null;
  onFetchLeads: () => void;
  onCreateManualLead: () => void;
  onLeadSelect: () => void;
};

const LeadSelect: FC<LeadSelectProps> = (props) => {
  const {
    lead,
    leadOptions,
    error,
    hasLead = false,
    loadingLeadsInProgress,
    onFetchLeads,
    onCreateManualLead,
    onLeadSelect,
  } = props;

  const hasError = !!error;
  const [leadValue, setLeadValue] = useState<string>();

  const { mutate: selectLeadMutation, isPending: isPendingSelectLead } =
    useTanstackMutation({
      onSuccess: (response: LeadApiResponse) => {
        queryClient.setQueryData(['lead', { id: lead._id }], (oldData: any) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            lead: response,
          };
        });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        queryClient.invalidateQueries({ queryKey: ['tabs'] });
      },
      onError: (error) => {
        console.error(error);
      },
    });

  const handleCreateManualLead = () => {
    setLeadValue(undefined);
    onCreateManualLead();
  };

  const selectLead = (leadId: string) =>
    selectLeadMutation({
      path: API_PATHS.leads.selectLead(lead._id, leadId),
      method: 'post',
    });

  const options = useMemo(() => {
    const getStatusWeight = (status: string) => {
      switch (status) {
        case 'verified':
          return 0;
        case 'extrapolated':
          return 1;
        default:
          return 2; // includes 'unavailable' or anything else
      }
    };

    return leadOptions
      ?.slice()
      .sort(
        (a, b) =>
          getStatusWeight(a.email_status) - getStatusWeight(b.email_status),
      )
      .map((lead) => {
        return {
          value: lead.id,
          label: (
            <div className="lead-option">
              <div className="lead-left">
                <img
                  src={
                    lead.photo_url ||
                    'https://static.licdn.com/aero-v1/sc/h/9c8pery4andzj6ohjkjp54ma2'
                  }
                  alt={`${lead.first_name} ${lead.last_name}`}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src =
                      'https://static.licdn.com/aero-v1/sc/h/9c8pery4andzj6ohjkjp54ma2';
                  }}
                  className="lead-photo"
                />
                <div>
                  <div className="lead-name">
                    <div>{`${lead.first_name || ''} ${
                      lead.last_name || ''
                    }`}</div>
                    <a
                      href={lead.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="linkedin-link"
                    >
                      <FaLinkedin className="linkedin-icon" />
                    </a>
                  </div>
                  <div className="lead-title">{lead.title}</div>
                </div>
              </div>
              {lead.has_email ? (
                <span className="email-status">
                  <FaCheckCircle
                    className="email-verified-icon"
                    title="Email verified"
                  />
                  Email verified
                </span>
              ) : (
                <span className="email-status">
                  <FaTimesCircle
                    className="email-unverified-icon"
                    title="Email unverified"
                  />
                  Email not available
                </span>
              )}
            </div>
          ),
          searchString: `${lead.first_name || ''} ${lead.last_name || ''}${
            lead.title ? ` (${lead.title})` : ''
          }`.trim(),
        };
      });
  }, [leadOptions]);

  useEffect(() => {
    if (!lead?.contact) return;
    setLeadValue(lead?.contact?.apollo_id);
  }, [lead]);

  return (
    <div className="lead-select">
      <div className="lead-select-field">
        {!options && (
          <div className="lead-select-field__wrapper">
            <Button
              onClick={onFetchLeads}
              isLoading={loadingLeadsInProgress}
              className="generate-leads-btn"
            >
              {hasLead ? (
                <>
                  <FaSync />
                  Change Lead
                </>
              ) : (
                'Find Leads'
              )}
            </Button>
            <span>or</span>
            <Button
              onClick={handleCreateManualLead}
              className="generate-leads-btn"
            >
              Create a new lead
            </Button>
          </div>
        )}
        {hasError && (
          <div className="error-message">
            {error?.message || 'Error loading leads'}
          </div>
        )}

        {options && (
          <div className="lead-select-field__wrapper">
            {options.length > 0 && (
              <SelectField
                options={options}
                onChange={(val) => {
                  onLeadSelect();
                  selectLead(val as unknown as string);
                  setLeadValue(val as unknown as string);
                }}
                placeholder="Select lead"
                value={leadValue}
              />
            )}
            <Button
              onClick={handleCreateManualLead}
              className="generate-leads-btn"
            >
              Create a new lead
            </Button>
          </div>
        )}
      </div>
      {isPendingSelectLead && <LoadingSpinner />}
    </div>
  );
};

export default LeadSelect;
